import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type InventoryStatus, listVehiclesSortBySchema, type VehicleSummary } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { formatDay, formatDays, formatMoney } from '../../lib/format';
import { useListSearchParams } from '../../lib/list-search-params';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { INVENTORY_STATUSES, inventoryStatusTone, vehicleTitle } from './vehicles-display';

/**
 * `/vehicles` — **purchased vehicles only** (plan §3.1, §7).
 *
 * The primary action is deliberately "Start an estimate" rather than "Add vehicle": there is no way to
 * put a car into this list except by buying one, and the page says so in its own subtitle. Everything
 * else follows the estimates list — server paging, filters and order in the URL (plan §11 fix 12).
 */
export const VehiclesListPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const params = useListSearchParams();

  const search = params.value('q');
  const statusFilter = params.value('status');
  const templateFilter = params.value('templateId');
  const assigneeFilter = params.value('assignedUserId');
  const purchasedFrom = params.value('purchaseDateFrom');
  const purchasedTo = params.value('purchaseDateTo');
  const soldFrom = params.value('saleDateFrom');
  const soldTo = params.value('saleDateTo');
  const sortBy = params.sortBy(listVehiclesSortBySchema.options, 'purchaseDate');
  const sortDirection = params.sortDirection('desc');

  const templatesQuery = useQuery(orpc.templates.list.queryOptions({ input: { page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc' } }));

  const listQuery = useQuery(
    orpc.vehicles.list.queryOptions({
      input: {
        page: params.page,
        pageSize: params.pageSize,
        sortBy,
        sortDirection,
        q: search.trim() === '' ? undefined : search.trim(),
        status: statusFilter === '' ? undefined : [statusFilter as InventoryStatus],
        templateId: templateFilter === '' ? undefined : templateFilter,
        assignedUserId: assigneeFilter === '' ? undefined : assigneeFilter,
        purchaseDateFrom: purchasedFrom === '' ? undefined : purchasedFrom,
        purchaseDateTo: purchasedTo === '' ? undefined : purchasedTo,
        saleDateFrom: soldFrom === '' ? undefined : soldFrom,
        saleDateTo: soldTo === '' ? undefined : soldTo,
      },
    }),
  );

  const assignees = useMemo(() => {
    const seen = new Map<string, string>();

    for (const row of listQuery.data?.items ?? []) {
      if (row.assignedUserId && row.assignedUserName) {
        seen.set(row.assignedUserId, row.assignedUserName);
      }
    }

    return [...seen.entries()].map(([id, name]) => ({ value: id, label: name }));
  }, [listQuery.data]);

  const columns = useMemo<DataTableColumns<VehicleSummary>>(
    () => [
      {
        id: 'stockNo',
        header: t('vehicles.columnVehicle'),
        accessorFn: (row) => row.stockNo,
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-mono text-sm font-medium text-foreground">{row.original.stockNo}</p>
            <p className="truncate text-xs text-muted-foreground">
              {[vehicleTitle(row.original), row.original.modelYear ?? null].filter((part) => part !== null && part !== '').join(' · ')}
            </p>
          </div>
        ),
      },
      {
        id: 'status',
        header: t('vehicles.columnStatus'),
        enableSorting: false,
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.inventoryStatus.${row.original.status}`)}
            tone={inventoryStatusTone(row.original.status)}
          />
        ),
      },
      {
        id: 'purchaseDate',
        header: t('vehicles.columnPurchased'),
        accessorFn: (row) => row.purchaseDate ?? '',
        cell: ({ row }) => (
          <InfoCell
            title={formatDay(row.original.purchaseDate)}
            subline={row.original.sourceEstimateNo}
          />
        ),
      },
      {
        id: 'purchasePrice',
        header: t('vehicles.columnPurchasePrice'),
        accessorFn: (row) => row.purchasePrice ?? 0,
        cell: ({ row }) => <Money value={row.original.purchasePrice} />,
      },
      {
        id: 'askingPrice',
        header: t('vehicles.columnAskingPrice'),
        accessorFn: (row) => row.askingPrice ?? 0,
        cell: ({ row }) => <Money value={row.original.askingPrice} />,
      },
      {
        id: 'daysInStock',
        header: t('vehicles.columnDaysInStock'),
        accessorFn: (row) => row.daysInStock ?? 0,
        // Computed by the database against the tenant's business day, and **frozen at the sale date**
        // once the car is sold (PP §3) — so this column stops moving exactly when the car leaves.
        cell: ({ row }) => (
          <span
            dir="ltr"
            className="inline-block tabular-nums text-foreground"
          >
            {formatDays(row.original.daysInStock)}
          </span>
        ),
      },
      {
        id: 'salePrice',
        header: t('vehicles.columnSalePrice'),
        accessorFn: (row) => row.salePrice ?? 0,
        // "Not recorded" rather than a zero somebody might read as "sold for nothing" (PP §3).
        cell: ({ row }) =>
          row.original.salePrice === null ? (
            <span className="text-sm text-muted-foreground">{t('common.notRecorded')}</span>
          ) : (
            <Money value={row.original.salePrice} />
          ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('vehicles.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('vehicles.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/estimates/new')}>
          <Plus className="size-4" />
          {t('vehicles.startEstimate')}
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <label
            htmlFor="vehicles-search"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('vehicles.search')}
          </label>
          <Input
            id="vehicles-search"
            value={search}
            placeholder={t('vehicles.searchPlaceholder')}
            onChange={(event) => params.update({ q: event.target.value })}
          />
        </div>
        <div className="w-48">
          <label
            htmlFor="vehicles-status"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('vehicles.columnStatus')}
          </label>
          <Select
            id="vehicles-status"
            value={statusFilter}
            placeholder={t('vehicles.allStatuses')}
            onChange={(event) => params.update({ status: event.target.value })}
            options={INVENTORY_STATUSES.map((status) => ({ value: status, label: t(`enums.inventoryStatus.${status}`) }))}
          />
        </div>
        <div className="w-48">
          <label
            htmlFor="vehicles-template"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('estimates.template')}
          </label>
          <Select
            id="vehicles-template"
            value={templateFilter}
            placeholder={t('common.all')}
            onChange={(event) => params.update({ templateId: event.target.value })}
            options={(templatesQuery.data?.items ?? []).map((template) => ({ value: template.id, label: template.name }))}
          />
        </div>
        <div className="w-44">
          <label
            htmlFor="vehicles-assignee"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('estimates.assignee')}
          </label>
          <Select
            id="vehicles-assignee"
            value={assigneeFilter}
            placeholder={t('common.all')}
            onChange={(event) => params.update({ assignedUserId: event.target.value })}
            options={assignees}
          />
        </div>
        <div className="w-40">
          <label
            htmlFor="vehicles-purchased-from"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('vehicles.purchasedFrom')}
          </label>
          <Input
            id="vehicles-purchased-from"
            type="date"
            dir="ltr"
            value={purchasedFrom}
            onChange={(event) => params.update({ purchaseDateFrom: event.target.value })}
          />
        </div>
        <div className="w-40">
          <label
            htmlFor="vehicles-purchased-to"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('vehicles.purchasedTo')}
          </label>
          <Input
            id="vehicles-purchased-to"
            type="date"
            dir="ltr"
            value={purchasedTo}
            onChange={(event) => params.update({ purchaseDateTo: event.target.value })}
          />
        </div>
        <div className="w-40">
          <label
            htmlFor="vehicles-sold-from"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('vehicles.soldFrom')}
          </label>
          <Input
            id="vehicles-sold-from"
            type="date"
            dir="ltr"
            value={soldFrom}
            onChange={(event) => params.update({ saleDateFrom: event.target.value })}
          />
        </div>
        <div className="w-40">
          <label
            htmlFor="vehicles-sold-to"
            className="mb-1.5 block text-xs font-medium text-muted-foreground"
          >
            {t('vehicles.soldTo')}
          </label>
          <Input
            id="vehicles-sold-to"
            type="date"
            dir="ltr"
            value={soldTo}
            onChange={(event) => params.update({ saleDateTo: event.target.value })}
          />
        </div>
      </div>

      {listQuery.isError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>
      ) : null}

      <DataTable
        columns={columns}
        data={listQuery.data?.items ?? []}
        isLoading={listQuery.isLoading}
        emptyMessage={t('vehicles.empty')}
        pagination={{ page: params.page, pageSize: params.pageSize, total: listQuery.data?.meta.total ?? 0, onPageChange: params.setPage }}
        sorting={{ sortBy, sortDirection, onSortChange: params.setSort }}
        onRowClick={(row) => navigate(`/vehicles/${row.id}`)}
      />
    </div>
  );
};

const Money = ({ value }: { value: number | null }) => (
  <span
    dir="ltr"
    className="inline-block tabular-nums text-foreground"
  >
    {formatMoney(value)}
  </span>
);
